/**
 * Library Page
 *
 * Simplified document management with two sections:
 * - Credentials: Licenses and Insurance certificates
 * - Documents: Reusable documents (W-9s, letterheads, etc.)
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  RefreshCw,
  ShieldCheck,
  FileText,
  Loader2,
} from "lucide-react";

import { DocumentLibrary } from "@/components/profile/DocumentLibrary";
import { LicenseManager } from "@/components/profile/LicenseManager";
import { InsuranceManager } from "@/components/profile/InsuranceManager";

export default function EvidenceVault() {
  const [loading, setLoading] = useState(true);
  const [contractorId, setContractorId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"credentials" | "documents">("credentials");

  const { toast } = useToast();
  const navigate = useNavigate();

  // Load contractor ID on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate("/auth");
          return;
        }

        // Get user document to determine contractorId
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        const userData = userDoc.data();
        const cId = userData?.contractorId || userData?.orgId || currentUser.uid;
        setContractorId(cId);
      } catch (error) {
        console.error("Error loading library:", error);
        toast({
          title: "Error",
          description: "Failed to load library",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        loadData();
      } else {
        navigate("/auth");
      }
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Library"
        actions={
          <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="h-8 sm:h-9">
            <RefreshCw className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        }
      />
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "credentials" | "documents")}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="credentials" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Credentials
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
          </TabsList>

          {/* Credentials Tab - Licenses and Insurance */}
          <TabsContent value="credentials" className="mt-4 space-y-6">
            {contractorId ? (
              <>
                <LicenseManager contractorId={contractorId} />
                <InsuranceManager contractorId={contractorId} />
              </>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents Tab - Reusable Documents */}
          <TabsContent value="documents" className="mt-4">
            {contractorId ? (
              <DocumentLibrary contractorId={contractorId} />
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
